import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUsers } from "../../../hooks/useGetUsers";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { updateProjet, updateIntervention,updateEtape, addProjetUtilisateurs, removeProjetUtilisateurs,addProjetGroupes,removeProjetGroupes  } from "../../../api/PROJETS/projets";
// import { useGetProjetGroupes } from "../../../hooks/PROJET/useGetProjetGroupes";
import { useGroupes } from "../../../hooks/useGetGroupes";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";
import { customSelectStyles } from "../../../utils/reactSelectStyles";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const _pad = (n) => String(n).padStart(2, "0");

// ISO string (avec ou sans offset) → "YYYY-MM-DD" pour <input type="date">
// Les chaînes date-seule (YYYY-MM-DD) sont parsées à midi local pour éviter
// le décalage d'un jour lié à minuit UTC.
const toDateInput = (s) => {
  if (!s) return "";
  const d = new Date(s.includes("T") ? s : `${s}T12:00:00`);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
};

// ISO string (avec ou sans offset) → "YYYY-MM-DDTHH:MM" pour <input type="datetime-local">
const toDatetimeInput = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
};

// "YYYY-MM-DDTHH:MM" → "YYYY-MM-DDTHH:MM:00Z" (UTC)
const toApiDatetime = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? null : d.toISOString().slice(0, 19) + "Z";
};

// "YYYY-MM-DD" → UTC ISO (minuit local converti en UTC)
const toApiDate = (s) => {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d) ? null : d.toISOString().slice(0, 19) + "Z";
};

// minutes → "HH:MM"
const minutesToTime = (min) => {
  if (min == null) return "00:30";
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
};

// "HH:MM" → minutes
const timeToMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};



// ─── 1. EditProjetModal ───────────────────────────────────────────────────────

export const EditProjetModal = ({ projet, show, onHide, onUpdated }) => {
  const queryClient = useQueryClient();
  const [titre, setTitre] = useState("");
  const [description, setDesc] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [checkDateFin, setCheckDateFin] = useState(false);
  const [selectedCollaborateurs, setSelectedCollaborateurs] = useState([]);
  const [selectedGroupes, setSelectedGroupes] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: usersData } = useUsers();
  const { data: groupesData } = useGroupes();


  const optionsCollaborateurs = useMemo(
    () =>
      usersData?.data?.map((u) => ({
        value: u.id,
        label: `${u.surname || ""} ${u.name || ""}`.trim(),
      })) || [],
    [usersData]
  );

  const optionsGroupes = useMemo(
  () =>
      groupesData?.data?.map((g) => ({
        value: g.id,
        label: g.name,
      })) || [],
    [groupesData]
  );

  useEffect(() => {
    if (show && projet) {
      setTitre(projet.title ?? "");
      setDesc(projet.description ?? "");
      setDateDebut(toDateInput(projet.start_date));
      setDateFin(toDateInput(projet.end_date));
      setCheckDateFin(!!projet.end_date);


      if (projet.users && optionsCollaborateurs.length > 0) {
        const currentUsers = projet.users
          .map((u) => optionsCollaborateurs.find((opt) => opt.value === u.id))
          .filter(Boolean);
        setSelectedCollaborateurs(currentUsers);
      } else {
        setSelectedCollaborateurs([]);
      }
      if (projet.groups && optionsGroupes.length > 0) {
        const currentGroups = projet.groups
          .map((g) => optionsGroupes.find((opt) => opt.value === g.id))
          .filter(Boolean);
        setSelectedGroupes(currentGroups);
      } else {
        setSelectedGroupes([]);
      }
    }
  }, [show, projet, optionsCollaborateurs, optionsGroupes]);
 

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titre.trim()) return toast.error("Le titre est requis");

    setSaving(true);
    try {
      const payload = {
        projet: {
          titre,
          description: description || null,
          date_debut: toApiDate(dateDebut),
          date_fin: checkDateFin && dateFin ? toApiDate(dateFin) : null,
        },
      };

      await updateProjet(projet.id, payload);

 
      const oldUserIds = (projet.users || []).map((u) => u.id);
      const newUserIds = selectedCollaborateurs.map((u) => u.value);

      const toAdd = newUserIds.filter((id) => !oldUserIds.includes(id));
      const toRemove = oldUserIds.filter((id) => !newUserIds.includes(id));

      if (toAdd.length > 0) await addProjetUtilisateurs(projet.id, toAdd);
      if (toRemove.length > 0) await removeProjetUtilisateurs(projet.id, toRemove);

      const oldGroupIds = (projet.groups || []).map((g) => g.id);
      const newGroupIds = selectedGroupes.map((g) => g.value);
      const gToAdd = newGroupIds.filter((id) => !oldGroupIds.includes(id));
      const gToRemove = oldGroupIds.filter((id) => !newGroupIds.includes(id));

      if (gToAdd.length > 0) await addProjetGroupes(projet.id, gToAdd);
      if (gToRemove.length > 0) await removeProjetGroupes(projet.id, gToRemove);

      toast.success("Projet modifié avec succès");
    
      queryClient.invalidateQueries({ queryKey: ["projet", projet.id.toString()] });
      
      onUpdated?.();
      onHide();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier le projet</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Titre *</Form.Label>
            <Form.Control
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Titre du projet"
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Date de début</Form.Label>
              <Form.Control
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Date de fin</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  checked={checkDateFin}
                  onChange={() => setCheckDateFin(!checkDateFin)}
                />
                <Form.Control
                  type="date"
                  disabled={!checkDateFin}
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                />
              </div>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Groupes (Assignés)</Form.Label>
            <Select
              isMulti
              options={optionsGroupes}
              value={selectedGroupes}
              onChange={setSelectedGroupes}
              placeholder="Sélectionner les groupes..."
              styles={customSelectStyles}
              closeMenuOnSelect={false}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Intervenants (Assignés)</Form.Label>
            <Select
              isMulti
              options={optionsCollaborateurs}
              value={selectedCollaborateurs}
              onChange={setSelectedCollaborateurs}
              placeholder="Sélectionner les intervenants..."
              styles={customSelectStyles}
              closeMenuOnSelect={false}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Description</Form.Label>
            <MarkdownEditor value={description} onChange={setDesc} rows={5} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant="info" type="submit" disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
// ─── 2. EditEtapeModal ────────────────────────────────────────────────────────

export const EditEtapeModal = ({ projetId, etape, show, onHide, onUpdated }) => {
  const [titre,       setTitre]  = useState("");
  const [description, setDesc]   = useState("");
  const [dateCloture, setDate]   = useState("");
  const [saving,      setSaving] = useState(false);

  useEffect(() => {
    if (show && etape) {
      setTitre(etape.title       ?? "");
      setDesc (etape.description ?? "");
      setDate (toDateInput(etape.close_date));
    }
  }, [show, etape]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titre.trim()) return toast.error("Le titre est requis");
    setSaving(true);
    try {
      await updateEtape(projetId, etape.id, {
        etape: {
          titre,
          description:  description || null,
          date_cloture: toApiDate(dateCloture),
        },
      });
      toast.success("Étape modifiée");
      onUpdated?.();
      onHide();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier l'étape</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Titre *</Form.Label>
            <Form.Control
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Titre de l'étape"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <MarkdownEditor value={description} onChange={setDesc} rows={3} />
          </Form.Group>

          <Form.Group>
            <Form.Label>Date de clôture prévue</Form.Label>
            <Form.Control
              type="date"
              value={dateCloture}
              onChange={(e) => setDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant="info" type="submit" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ─── 3. EditInterventionModal ─────────────────────────────────────────────────

export const EditInterventionModal = ({
  projetId,
  intervention,
  etapes = [],
  show,
  onHide,
  onUpdated,
  usersData,
}) => {
  const [description,   setDesc]   = useState("");
  const [dateDebut,     setDate]   = useState("");
  const [duree,         setDuree]  = useState("00:30");
  const [selectedUser,  setUser]   = useState(null);
  const [selectedEtape, setEtape]  = useState(null);
  const [saving,        setSaving] = useState(false);

  const userOptions  = (usersData?.data ?? [])
    .filter((u) => u.is_active)
    .map((u) => ({ value: u.id, label: `${u.name} ${u.surname}`.trim() }));

  const etapeOptions = etapes.map((e) => ({ value: e.id, label: e.title }));

  useEffect(() => {
    if (show && intervention) {
      setDesc (intervention.description ?? "");
      setDate (toDatetimeInput(intervention.start_date));
      setDuree(minutesToTime(intervention.duration));

      // Pré-remplir l'utilisateur
      if (intervention.user) {
        setUser({
          value: intervention.user.id,
          label: `${intervention.user.name} ${intervention.user.surname}`.trim(),
        });
      } else {
        setUser(null);
      }

      // Pré-remplir l'étape parente (cherche l'étape qui contient cette intervention)
      const parentEtape = etapes.find((e) =>
        e.project_interventions?.some((i) => i.id === intervention.id)
      );
      setEtape(parentEtape ? { value: parentEtape.id, label: parentEtape.title } : null);
    }
  }, [show, intervention, etapes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error("La description est requise");
    if (!dateDebut)           return toast.error("La date est requise");
    if (!selectedUser)        return toast.error("L'intervenant est requis");
    if (!selectedEtape)       return toast.error("L'étape est requise");

    setSaving(true);
    try {
      await updateIntervention(projetId, intervention.id, {
        intervention: {
          description,
          date_debut:  toApiDatetime(dateDebut),
          duree:       timeToMinutes(duree),
          utilisateur: selectedUser.value,
          etape:       selectedEtape.value,
        },
      });
      toast.success("Intervention modifiée");
      onUpdated?.();
      onHide();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier l'intervention</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Étape *</Form.Label>
            <Select
              options={etapeOptions}
              value={selectedEtape}
              onChange={setEtape}
              styles={customSelectStyles}
              placeholder="Choisir une étape…"
            />
          </Form.Group>

          <Row className="mb-3">
            <Form.Group as={Col} md={7}>
              <Form.Label>Date et heure *</Form.Label>
              <Form.Control
                type="datetime-local"
                value={dateDebut}
                onChange={(e) => setDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md={5}>
              <Form.Label>Durée *</Form.Label>
              <Form.Control
                type="time"
                value={duree}
                onChange={(e) => setDuree(e.target.value)}
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Intervenant *</Form.Label>
            <Select
              options={userOptions}
              value={selectedUser}
              onChange={setUser}
              styles={customSelectStyles}
              placeholder="Choisir un intervenant…"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Description *</Form.Label>
            <MarkdownEditor
              value={description}
              onChange={setDesc}
              rows={3}
              placeholder="Détail des actions réalisées…"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant="warning" type="submit" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};